import React, {
  useState,
  useCallback,
  FormEvent,
  useRef,
  useEffect,
} from "react";
import BackButton from "../shared/BackButton/BackButton";
import {
  buildRandomTopicPrompt,
  buildPassagePrompt,
  buildPassageInitialImagePromptPayload,
  buildPassageCustomImagePayload,
} from "../GenAIPrompts/GenAIPrompts";
import styles from "./Passages.module.css";
import { callOpenAI, generateImage, mobileAppUri } from "../API/API";
import apiClient from "../services/apiClient";
import {
  fetchPassages,
  addPassage,
  updatePassage,
  deletePassage,
} from "../API/Passages/PassagesAPI";
import { PassageData, VocabularyData } from "../API/types";
import { v4 as uuidv4 } from "uuid";
import { fetchVocabularyWords } from "../API/Vocabulary/VocabularyAPI";

// --- Constants ---
const AVAILABLE_LANGUAGES = [
  "Spanish",
  "English",
  "Italian",
  "French",
  "German",
  "Portuguese",
];
const PROFICIENCY_LEVELS = Array.from({ length: 10 }, (_, i) =>
  (i + 1).toString()
);

// --- Interfaces and Types ---
interface GenreTopics {
  genre: string;
  topics: string[];
}

// Extends PassageData with local-only fields used during generation/editing
type GeneratedPassageData = PassageData & {
  customPrompt?: string; // Optional custom prompt for image generation
  isRegenerating?: boolean; // Flag for image regeneration UI state
};

// Wrapper for generated passages list to include the original topic
interface GeneratedPassage {
  passage: GeneratedPassageData;
  topic: string;
}

// Type for distinguishing edit mode in the modal
type EditMode = "new" | "existing";

// --- Component ---
const Passages: React.FC = () => {
  // --- State Variables ---
  const [language, setLanguage] = useState<string>("");
  const [proficiency, setProficiency] = useState("1");
  const [newGenre, setNewGenre] = useState("");
  const [genres, setGenres] = useState<string[]>([]);
  const [topicsCount, setTopicsCount] = useState<number>(1);
  const [topicsState, setTopicsState] = useState<GenreTopics[]>([]);
  const [passagesState, setPassagesState] = useState<GeneratedPassage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false); // General loading for AI/Save ops
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState<
    GeneratedPassageData | PassageData | null
  >(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMode, setEditingMode] = useState<EditMode>("new");
  const [section, setSection] = useState<string>("");
  const [sections, setSections] = useState<string[]>([]);
  const [existingPassages, setExistingPassages] = useState<PassageData[]>([]);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const imagesGeneratedRef = useRef(false);
  // at the top with your other useState’s:
  const [vocabItems, setVocabItems] = useState<VocabularyData[]>([]);
  const [loadingVocab, setLoadingVocab] = useState(false);
  // and track a set of the selected *IDs*:
const [selectedWordIds, setSelectedWordIds] = useState<Set<string>>(new Set());
  
  // State for prompt preview
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [isPromptModified, setIsPromptModified] = useState(false);
  
  // State for feedback modal
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackPassageIndex, setFeedbackPassageIndex] = useState<number | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // State for image feedback modal
  const [imageFeedbackModalOpen, setImageFeedbackModalOpen] = useState(false);
  const [imageFeedbackText, setImageFeedbackText] = useState("");
  const [imageFeedbackPassageIndex, setImageFeedbackPassageIndex] = useState<number | null>(null);
  
  // State for existing passage feedback
  const [existingPassageFeedbackModalOpen, setExistingPassageFeedbackModalOpen] = useState(false);
  const [existingPassageFeedbackText, setExistingPassageFeedbackText] = useState("");
  const [existingPassageFeedbackData, setExistingPassageFeedbackData] = useState<PassageData | null>(null);
  
  // State for existing image feedback
  const [existingImageFeedbackModalOpen, setExistingImageFeedbackModalOpen] = useState(false);
  const [existingImageFeedbackText, setExistingImageFeedbackText] = useState("");
  const [existingImageFeedbackData, setExistingImageFeedbackData] = useState<PassageData | null>(null);
  
  // State for review modal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [originalPassage, setOriginalPassage] = useState<PassageData | null>(null);
  const [regeneratedPassage, setRegeneratedPassage] = useState<PassageData | null>(null);
  const [reviewType, setReviewType] = useState<'passage' | 'image'>('passage');

  // Debug effect for modal state
  useEffect(() => {
    console.log("Feedback modal state:", feedbackModalOpen, "Index:", feedbackPassageIndex);
  }, [feedbackModalOpen, feedbackPassageIndex]);

// Fetch and immediately extract only the Targ_Word strings:
useEffect(() => {
  async function loadVocab() {
    if (!language || !section) {
      setVocabItems([]);
      return;
    }
    setLoadingVocab(true);
    try {
      // Map language names to codes for the API
      const langCodeMap = {
        "Spanish": "ES",
        "English": "EN", 
        "French": "FR",
        "German": "DE",
        "Italian": "IT",
        "Portuguese": "PT"
      };
      
      const targetLangCode = langCodeMap[language as keyof typeof langCodeMap] || "ES";
      const baseLangCode = "EN"; // Assume English as base for data entry
      
      console.log(`Loading passages vocabulary for section: ${section}, target: ${targetLangCode}, base: ${baseLangCode}`);
      const data: VocabularyData[] = await fetchVocabularyWords(section, targetLangCode, baseLangCode);

      // dedupe by Targ_Word, keeping the first occurrence:
      const uniqueByWord = Array.from(
        data.reduce<Map<string, VocabularyData>>((map, item) => {
          if (item.Targ_Word && !map.has(item.Targ_Word)) {
            map.set(item.Targ_Word, item);
          }
          return map;
        }, new Map()).values()
      );

      setVocabItems(uniqueByWord);
      setSelectedWordIds(new Set());
    } catch (err) {
      console.error("Failed loading vocab:", err);
      setVocabItems([]);
    } finally {
      setLoadingVocab(false);
    }
  }
  loadVocab();
}, [language, section]);



  // --- Helper Functions ---
  const clearError = () => setError(null);
  const getLangCode = (langName: string): string => {
    const map: { [key: string]: string } = {
      Spanish: "ES",
      English: "EN",
      Italian: "IT",
      French: "FR",
      German: "DE",
      Portuguese: "PT",
    };
    return map[langName] || langName.substring(0, 2).toUpperCase();
  };

  // --- Event Handlers --- (Language, Section, Genre Add/Remove)

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    setGenres([]);
    setTopicsState([]);
    setPassagesState([]);
    setSection("");
    setSections([]);
    setExistingPassages([]);
    clearError();
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSection(e.target.value);
    clearError();
  };

  const handleAddGenre = (e: FormEvent) => {
    e.preventDefault();
    clearError();
    const trimmed = newGenre.trim();
    if (!trimmed) return;
    if (!language || !section) {
      setError("Please select language and section first.");
      return;
    }
    if (genres.includes(trimmed)) {
      setError(`Genre "${trimmed}" already added.`);
      return;
    }
    setGenres((prev) => [...prev, trimmed]);
    setNewGenre("");
  };

  const removeGenre = (indexToRemove: number) => {
    const genreToRemove = genres[indexToRemove];
    setGenres((prev) => prev.filter((_, i) => i !== indexToRemove));
    setTopicsState((prev) =>
      prev.filter((item) => item.genre !== genreToRemove)
    );
    setPassagesState((prev) =>
      prev.filter((p) => p.passage.Genre !== genreToRemove)
    );
  };

  // --- Core Logic Functions (Generate Topics/Passages/Images) ---

  const handleGenerateTopics = useCallback(async () => {
    clearError();
    if (!language || !section) {
      setError("Please select language and section.");
      return;
    }
    if (genres.length === 0) {
      setError("Please add at least one genre.");
      return;
    }
    if (topicsCount < 1) {
      setError("Topics/Passages count must be at least 1.");
      return;
    }
    setIsGenerating(true);
    setTopicsState([]);
    setPassagesState([]);

    try {
      const newTopicsState: GenreTopics[] = [];
      for (const genre of genres) {
        const payload = buildRandomTopicPrompt(
          genre,
          section,
          topicsCount,
          language
        );
        // Uses imported callOpenAI (which uses centralized URI)
        const result = await callOpenAI(payload);
        if (!result || !result.response)
          throw new Error(
            `API did not return a valid response for genre "${genre}".`
          );

        let jsonString = result.response;
        const startIndex = jsonString.indexOf("[");
        const endIndex = jsonString.lastIndexOf("]") + 1;
        if (startIndex === -1 || endIndex === 0)
          throw new Error(
            `Could not find JSON array structure for genre "${genre}". Response: ${jsonString}`
          );
        jsonString = jsonString.slice(startIndex, endIndex).trim();

        let topicsArray;
        try {
          topicsArray = JSON.parse(jsonString);
        } catch (parseError: any) {
          throw new Error(
            `Failed to parse JSON topics for genre "${genre}". Error: ${parseError.message}. Received: ${jsonString}`
          );
        }
        if (!Array.isArray(topicsArray))
          throw new Error(
            `Parsed response for genre "${genre}" is not an array.`
          );

        const validatedTopics: string[] = topicsArray
          .map((t: any) => String(t || "").trim())
          .filter((t) => t);
        newTopicsState.push({ genre, topics: validatedTopics });
      }
      setTopicsState(newTopicsState);
    } catch (err: any) {
      console.error("Error generating topics:", err);
      setError(err.message || "Unknown error during topic generation.");
    } finally {
      setIsGenerating(false);
    }
  }, [genres, topicsCount, language, section, proficiency]); // buildRandomTopicPrompt assumed stable

  const handleRegenerateTopics = useCallback(
    async (genreIndex: number) => {
      clearError();
      const genre = genres[genreIndex];
      if (!language || !section || !genre) {
        setError("Missing Language, Section, or Genre for regeneration.");
        return;
      }
      if (topicsCount < 1) {
        setError("Topics/Passages count must be at least 1.");
        return;
      }
      setIsGenerating(true);

      try {
        const payload = buildRandomTopicPrompt(
          genre,
          section,
          topicsCount,
          language
        );
        // Uses imported callOpenAI
        const result = await callOpenAI(payload);
        if (!result || !result.response)
          throw new Error(
            `API did not return valid response for genre "${genre}".`
          );

        let jsonString = result.response;
        const startIndex = jsonString.indexOf("[");
        const endIndex = jsonString.lastIndexOf("]") + 1;
        if (startIndex === -1 || endIndex === 0)
          throw new Error(
            `Could not find JSON array structure for genre "${genre}". Response: ${jsonString}`
          );
        jsonString = jsonString.slice(startIndex, endIndex).trim();

        let topicsArray;
        try {
          topicsArray = JSON.parse(jsonString);
        } catch (parseError: any) {
          throw new Error(
            `Failed to parse JSON topics for genre "${genre}". Error: ${parseError.message}. Received: ${jsonString}`
          );
        }
        if (!Array.isArray(topicsArray))
          throw new Error(
            `Parsed response for genre "${genre}" is not an array.`
          );

        const validatedTopics: string[] = topicsArray
          .map((t: any) => String(t || "").trim())
          .filter((t) => t);
        setTopicsState((prev) =>
          prev.map((item, idx) =>
            idx === genreIndex ? { ...item, topics: validatedTopics } : item
          )
        );
        setPassagesState((prev) =>
          prev.filter((p) => p.passage.Genre !== genre)
        ); // Clear related passages
      } catch (err: any) {
        console.error("Error regenerating topics:", err);
        setError(err.message || "Unknown error during topic regeneration.");
      } finally {
        setIsGenerating(false);
      }
    },
    [genres, topicsCount, language, section, proficiency] // buildRandomTopicPrompt assumed stable
  );

  // Function to generate prompt preview
  const generatePromptPreview = useCallback(() => {
    if (!language || !section || topicsState.length === 0) {
      setCustomPrompt("Please select language, section, and generate topics first.");
      return;
    }

    const firstTopic = topicsState.find(ts => ts.topics.length > 0);
    if (!firstTopic) {
      setCustomPrompt("No topics available for preview.");
      return;
    }

    const wordsArray = Array.from(selectedWordIds).map((id) => {
      const item = vocabItems.find((v) => v.Identifier === id);
      return item?.Targ_Word ?? "";
    });

    const payload = buildPassagePrompt(
      firstTopic.genre,
      firstTopic.topics[0],
      section,
      proficiency,
      wordsArray,
      language
    );

    const promptText = payload.messages[0].content;
    setCustomPrompt(promptText);
    setIsPromptModified(false);
  }, [language, section, topicsState, proficiency, selectedWordIds, vocabItems]);

  const handleGeneratePassages = useCallback(async () => {
    clearError();
    if (!language || !section) {
      setError("Please select language and section first.");
      return;
    }
    if (
      topicsState.length === 0 ||
      topicsState.every((ts) => ts.topics.length === 0)
    ) {
      setError("No topics available. Please generate topics first.");
      return;
    }
    setIsGenerating(true);
    setPassagesState([]);
    imagesGeneratedRef.current = false;

    try {
      const newPassages: GeneratedPassage[] = [];
      const allTopics = topicsState.flatMap((gt) =>
        gt.topics.map((topic) => ({ genre: gt.genre, topic }))
      );

      for (const { genre, topic } of allTopics) {
             // look up each selected ID’s word before passing into the prompt
      const wordsArray = Array.from(selectedWordIds).map((id) => {
        const item = vocabItems.find((v) => v.Identifier === id);
        return item?.Targ_Word ?? "";
      });
        let payload;
        if (isPromptModified && customPrompt) {
          // Use custom prompt if it's been modified
          payload = {
            messages: [
              {
                role: "user",
                content: customPrompt,
              },
            ],
            temperature: 0.7,
            max_tokens: 2000,
          };
        } else {
          // Use default prompt generation
          payload = buildPassagePrompt(
            genre,
            topic,
            section,
            proficiency,
            wordsArray,
            language
          );
        }
        
        // Uses imported callOpenAI
        const result = await callOpenAI(payload);
        if (!result || !result.response) {
          console.warn(`API invalid response for topic "${topic}". Skipping.`);
          continue;
        }

        let jsonString = result.response;
        const startIndex = jsonString.indexOf("{");
        const endIndex = jsonString.lastIndexOf("}") + 1;
        if (startIndex === -1 || endIndex === 0) {
          console.warn(
            `Could not find JSON object for topic "${topic}". Skipping. Response: ${jsonString}`
          );
          continue;
        }
        jsonString = jsonString.slice(startIndex, endIndex).trim();

        let parsed;
        try {
          parsed = JSON.parse(jsonString);
        } catch (parseError: any) {
          console.warn(
            `Failed to parse JSON passage for topic "${topic}". Skipping. Error: ${parseError.message}. Received: ${jsonString}`
          );
          continue;
        }
        if (!parsed.Passage && !parsed.Targ_Lang_Passage) {
          console.warn(
            `Parsed data for topic "${topic}" missing 'Passage' field. Skipping.`
          );
          continue;
        }

        // --- Construct Passage Data (FIX for TS2322) ---
        // Initialize ALL fields required by PassageData, using parsed data or defaults
        const passageData: GeneratedPassageData = {
          Identifier: "",
          Level: section,
          Genre: genre,
          Section: section,
          Targ_Lang_Code: language, // Use state language
          Targ_Lang_Title:
            parsed.Name || parsed.Targ_Lang_Title || `Passage for ${topic}`,
          Targ_Lang_Description:
            parsed.Description ||
            parsed.Targ_Lang_Description ||
            `Description for ${topic}`,
          Targ_Lang_Passage: parsed.Passage || parsed.Targ_Lang_Passage || "",
          Targ_Passage_Word_Timings: parsed.Targ_Passage_Word_Timings || "", // Ensure defaults
          Targ_Passage_Audio_URL: parsed.Targ_Passage_Audio_URL || "",
          Targ_Lang_Question_1:
            parsed.Question_1 || parsed.Targ_Lang_Question_1 || "",
          Targ_Lang_Answer_1:
            parsed.Answer_1 || parsed.Targ_Lang_Answer_1 || "",
          Targ_Lang_Options_1: JSON.stringify(
            parsed.Options_1 || parsed.Targ_Lang_Options_1 || []
          ),
          Targ_Lang_Question_2:
            parsed.Question_2 || parsed.Targ_Lang_Question_2 || "",
          Targ_Lang_Answer_2:
            parsed.Answer_2 || parsed.Targ_Lang_Answer_2 || "",
          Targ_Lang_Options_2: JSON.stringify(
            parsed.Options_2 || parsed.Targ_Lang_Options_2 || []
          ),
          Targ_Lang_Question_3:
            parsed.Question_3 || parsed.Targ_Lang_Question_3 || "",
          Targ_Lang_Answer_3:
            parsed.Answer_3 || parsed.Targ_Lang_Answer_3 || "",
          Targ_Lang_Options_3: JSON.stringify(
            parsed.Options_3 || parsed.Targ_Lang_Options_3 || []
          ),
          Targ_Lang_Question_4:
            parsed.Question_4 || parsed.Targ_Lang_Question_4 || "",
          Targ_Lang_Answer_4:
            parsed.Answer_4 || parsed.Targ_Lang_Answer_4 || "",
          Targ_Lang_Options_4: JSON.stringify(
            parsed.Options_4 || parsed.Targ_Lang_Options_4 || []
          ),
          ImageURL: "", // Initialize image URL
          Prompt: "", // Initialize prompt
          // Local fields
          customPrompt: "",
          isRegenerating: false,
          SelectedWordIds: Array.from(selectedWordIds).join(","),
        };
        newPassages.push({ topic, passage: passageData });
      } // End loop through topics

      if (newPassages.length === 0 && allTopics.length > 0)
        setError(
          "Passage generation completed, but no valid passages could be created."
        );
      setPassagesState(newPassages);
    } catch (err: any) {
      console.error("Error generating passages:", err);
      setError(err.message || "Unknown error during passage generation.");
    } finally {
      setIsGenerating(false);
    }
  }, [topicsState, proficiency, language, section, selectedWordIds, vocabItems, customPrompt, isPromptModified]); // buildPassagePrompt assumed stable

  // Handle feedback modal
  const openFeedbackModal = useCallback((index: number) => {
    console.log("Opening feedback modal for passage index:", index);
    setFeedbackPassageIndex(index);
    setFeedbackText("");
    setFeedbackModalOpen(true);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackModalOpen(false);
    setFeedbackText("");
    setFeedbackPassageIndex(null);
  }, []);

  // Handle image feedback modal
  const openImageFeedbackModal = useCallback((index: number) => {
    console.log("Opening image feedback modal for passage index:", index);
    setImageFeedbackPassageIndex(index);
    setImageFeedbackText("");
    setImageFeedbackModalOpen(true);
  }, []);

  const closeImageFeedbackModal = useCallback(() => {
    setImageFeedbackModalOpen(false);
    setImageFeedbackText("");
    setImageFeedbackPassageIndex(null);
  }, []);

  // Handle existing passage feedback modal
  const openExistingPassageFeedbackModal = useCallback((passage: PassageData) => {
    console.log("Opening existing passage feedback modal for:", passage.Identifier);
    setExistingPassageFeedbackData(passage);
    setExistingPassageFeedbackText("");
    setExistingPassageFeedbackModalOpen(true);
  }, []);

  const closeExistingPassageFeedbackModal = useCallback(() => {
    setExistingPassageFeedbackModalOpen(false);
    setExistingPassageFeedbackText("");
    setExistingPassageFeedbackData(null);
  }, []);

  // Handle existing image feedback modal
  const openExistingImageFeedbackModal = useCallback((passage: PassageData) => {
    console.log("Opening existing image feedback modal for:", passage.Identifier);
    setExistingImageFeedbackData(passage);
    setExistingImageFeedbackText("");
    setExistingImageFeedbackModalOpen(true);
  }, []);

  const closeExistingImageFeedbackModal = useCallback(() => {
    setExistingImageFeedbackModalOpen(false);
    setExistingImageFeedbackText("");
    setExistingImageFeedbackData(null);
  }, []);

  // Handle review modal
  const openReviewModal = useCallback((original: PassageData, regenerated: PassageData, type: 'passage' | 'image') => {
    setOriginalPassage(original);
    setRegeneratedPassage(regenerated);
    setReviewType(type);
    setReviewModalOpen(true);
  }, []);

  const closeReviewModal = useCallback(() => {
    setReviewModalOpen(false);
    setOriginalPassage(null);
    setRegeneratedPassage(null);
    setReviewType('passage');
  }, []);

  const handleApproveRegeneration = useCallback(async () => {
    if (!originalPassage || !regeneratedPassage) return;
    
    try {
      // Save the regenerated passage
      await updatePassage(originalPassage.Identifier, originalPassage.Level, regeneratedPassage);
      
      // Refresh the existing passages list
      const fetchedPassages = await fetchPassages(section);
      setExistingPassages(fetchedPassages);
      
      closeReviewModal();
    } catch (err: any) {
      console.error("Error approving regeneration:", err);
      setError(err.message || "Failed to save approved changes.");
    }
  }, [originalPassage, regeneratedPassage, section, closeReviewModal]);

  const handleRejectRegeneration = useCallback(() => {
    // Simply close the modal without saving
    closeReviewModal();
  }, [closeReviewModal]);

  // Regenerate passage with feedback
  const handleRegenerateWithFeedback = useCallback(async () => {
    if (feedbackPassageIndex === null || !feedbackText.trim()) {
      setError("Please provide feedback before regenerating.");
      return;
    }

    const passageItem = passagesState[feedbackPassageIndex];
    if (!passageItem) {
      setError("Passage not found.");
      return;
    }

    setIsRegenerating(true);
    
    try {
      // Get the original prompt components
      const wordsArray = Array.from(selectedWordIds).map((id) => {
        const item = vocabItems.find((v) => v.Identifier === id);
        return item?.Targ_Word ?? "";
      });

      // Create feedback-based regeneration prompt
      const feedbackPrompt = `Based on the following feedback, please regenerate and improve this passage:

ORIGINAL PASSAGE:
Title: ${passageItem.passage.Targ_Lang_Title}
Description: ${passageItem.passage.Targ_Lang_Description}
Passage: ${passageItem.passage.Targ_Lang_Passage}

FEEDBACK TO ADDRESS:
${feedbackText}

Please regenerate the passage addressing the feedback while maintaining:
- Genre: ${passageItem.passage.Genre}
- Topic: ${passageItem.topic}
- Section: ${section}
- Language: ${language}
- Vocabulary: ${wordsArray.join(", ")}

Ensure that the JSON response strictly follows this structure:

{
  "Targ_Lang_Title": "Passage Title",
  "Targ_Lang_Description": "Short description of the passage",
  "Targ_Lang_Passage": "The full text of the passage. Use \\n to indicate line breaks between paragraphs.",
  "Targ_Lang_Question_1": "First question about the passage",
  "Targ_Lang_Answer_1": "The correct answer to Question 1",
  "Targ_Lang_Options_1": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_2": "Second question about the passage",
  "Targ_Lang_Answer_2": "The correct answer to Question 2",
  "Targ_Lang_Options_2": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_3": "Third question about the passage",
  "Targ_Lang_Answer_3": "The correct answer to Question 3",
  "Targ_Lang_Options_3": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_4": "Fourth question about the passage",
  "Targ_Lang_Answer_4": "The correct answer to Question 4",
  "Targ_Lang_Options_4": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ]
}

**Important Instructions:**
- **Use \\n** to indicate line breaks between paragraphs in the "Passage" field.
- **Exactly four options** must be provided for each question.
- One of the options must **explicitly be the correct answer**.
- The JSON response should **strictly adhere** to the provided structure with no additional text or commentary.
- ****All text returned should be in ${language}******.
- **Output only** the JSON object and nothing else.`;

      const payload = {
        messages: [
          {
            role: "user",
            content: feedbackPrompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 2000,
      };

      const result = await callOpenAI(payload);
      if (!result || !result.response) {
        setError("Failed to regenerate passage. Please try again.");
        return;
      }

      let jsonString = result.response;
      const startIndex = jsonString.indexOf("{");
      const endIndex = jsonString.lastIndexOf("}") + 1;
      if (startIndex === -1 || endIndex === 0) {
        setError("Invalid response format from AI. Please try again.");
        return;
      }

      jsonString = jsonString.substring(startIndex, endIndex);

      let parsed;
      try {
        parsed = JSON.parse(jsonString);
      } catch (parseError: any) {
        setError(`Failed to parse regenerated passage: ${parseError.message}`);
        return;
      }

      // Update the passage in the state
      const updatedPassageData: GeneratedPassageData = {
        ...passageItem.passage,
        Targ_Lang_Title: parsed.Targ_Lang_Title || passageItem.passage.Targ_Lang_Title,
        Targ_Lang_Description: parsed.Targ_Lang_Description || passageItem.passage.Targ_Lang_Description,
        Targ_Lang_Passage: parsed.Targ_Lang_Passage || passageItem.passage.Targ_Lang_Passage,
        Targ_Lang_Question_1: parsed.Targ_Lang_Question_1 || passageItem.passage.Targ_Lang_Question_1,
        Targ_Lang_Answer_1: parsed.Targ_Lang_Answer_1 || passageItem.passage.Targ_Lang_Answer_1,
        Targ_Lang_Options_1: JSON.stringify(parsed.Targ_Lang_Options_1 || JSON.parse(passageItem.passage.Targ_Lang_Options_1 || "[]")),
        Targ_Lang_Question_2: parsed.Targ_Lang_Question_2 || passageItem.passage.Targ_Lang_Question_2,
        Targ_Lang_Answer_2: parsed.Targ_Lang_Answer_2 || passageItem.passage.Targ_Lang_Answer_2,
        Targ_Lang_Options_2: JSON.stringify(parsed.Targ_Lang_Options_2 || JSON.parse(passageItem.passage.Targ_Lang_Options_2 || "[]")),
        Targ_Lang_Question_3: parsed.Targ_Lang_Question_3 || passageItem.passage.Targ_Lang_Question_3,
        Targ_Lang_Answer_3: parsed.Targ_Lang_Answer_3 || passageItem.passage.Targ_Lang_Answer_3,
        Targ_Lang_Options_3: JSON.stringify(parsed.Targ_Lang_Options_3 || JSON.parse(passageItem.passage.Targ_Lang_Options_3 || "[]")),
        Targ_Lang_Question_4: parsed.Targ_Lang_Question_4 || passageItem.passage.Targ_Lang_Question_4,
        Targ_Lang_Answer_4: parsed.Targ_Lang_Answer_4 || passageItem.passage.Targ_Lang_Answer_4,
        Targ_Lang_Options_4: JSON.stringify(parsed.Targ_Lang_Options_4 || JSON.parse(passageItem.passage.Targ_Lang_Options_4 || "[]")),
      };

      setPassagesState((prev) =>
        prev.map((item, i) =>
          i === feedbackPassageIndex
            ? { ...item, passage: updatedPassageData }
            : item
        )
      );

      closeFeedbackModal();
    } catch (err: any) {
      console.error("Error regenerating passage with feedback:", err);
      setError(err.message || "Unknown error during passage regeneration.");
    } finally {
      setIsRegenerating(false);
    }
  }, [feedbackPassageIndex, feedbackText, passagesState, selectedWordIds, vocabItems, section, language, closeFeedbackModal]);

  // Regenerate image with feedback
  const handleRegenerateImageWithFeedback = useCallback(async () => {
    if (imageFeedbackPassageIndex === null || !imageFeedbackText.trim()) {
      setError("Please provide feedback before regenerating the image.");
      return;
    }

    const passageItem = passagesState[imageFeedbackPassageIndex];
    if (!passageItem) {
      setError("Passage not found.");
      return;
    }

    // Set regenerating state for this specific passage
    setPassagesState((prev) =>
      prev.map((item, i) =>
        i === imageFeedbackPassageIndex
          ? { ...item, passage: { ...item.passage, isRegenerating: true } }
          : item
      )
    );

    try {
      const cleanPassage = passageItem.passage.Targ_Lang_Passage.replace(/\\n/g, "\n");
      
      // Create feedback-enhanced image prompt
      const feedbackPrompt = `IMPORTANT: THE IMAGE SHOULD NOT CONTAIN ANY TEXT, LETTERS, NUMBERS, OR WRITING. Based on the following feedback, please create an improved image: ${imageFeedbackText}

Create an image based on this passage: "${cleanPassage}"

FEEDBACK TO ADDRESS: ${imageFeedbackText}

Style requirements:
- Do not include any text, letters, numbers, or writing in the image
- It should be animated yet somewhat realistic
- Make it lively with the subject clearly shown
- Easy to identify for ${language} learners
- The image should be lively, engaging, and detailed

THE IMAGE SHOULD NOT CONTAIN ANY TEXT, LETTERS, NUMBERS, OR WRITING ANYWHERE IN THE IMAGE.`;

      const payload = {
        prompt: feedbackPrompt,
        model: "dall-e-3",
        n: 1,
        size: "1024x1024",
        response_format: "url",
      };

      console.log(`Regenerating image with feedback for index ${imageFeedbackPassageIndex}...`);
      const imageUrl = await generateImage(payload);
      
      if (!imageUrl) {
        throw new Error("API returned no image URL");
      }

      // Update the image URL in the passage state
      setPassagesState((prev) =>
        prev.map((item, i) =>
          i === imageFeedbackPassageIndex
            ? { 
                ...item, 
                passage: { 
                  ...item.passage, 
                  ImageURL: imageUrl,
                  isRegenerating: false 
                } 
              }
            : item
        )
      );

      closeImageFeedbackModal();
    } catch (err: any) {
      console.error("Error regenerating image with feedback:", err);
      setError(err.message || "Unknown error during image regeneration.");
      
      // Reset regenerating state on error
      setPassagesState((prev) =>
        prev.map((item, i) =>
          i === imageFeedbackPassageIndex
            ? { ...item, passage: { ...item.passage, isRegenerating: false } }
            : item
        )
      );
    }
  }, [imageFeedbackPassageIndex, imageFeedbackText, passagesState, language, closeImageFeedbackModal]);

  // Regenerate existing passage with feedback
  const handleRegenerateExistingPassageWithFeedback = useCallback(async () => {
    if (!existingPassageFeedbackData || !existingPassageFeedbackText.trim()) {
      setError("Please provide feedback before regenerating.");
      return;
    }

    setIsRegenerating(true);
    
    try {
      // Get the vocabulary for this passage
      const wordsArray = Array.from(selectedWordIds).map((id) => {
        const item = vocabItems.find((v) => v.Identifier === id);
        return item?.Targ_Word ?? "";
      });

      // Create feedback-based regeneration prompt
      const feedbackPrompt = `Based on the following feedback, please regenerate and improve this passage:

ORIGINAL PASSAGE:
Title: ${existingPassageFeedbackData.Targ_Lang_Title}
Description: ${existingPassageFeedbackData.Targ_Lang_Description}
Passage: ${existingPassageFeedbackData.Targ_Lang_Passage}

FEEDBACK TO ADDRESS:
${existingPassageFeedbackText}

Please regenerate the passage addressing the feedback while maintaining:
- Genre: ${existingPassageFeedbackData.Genre}
- Section: ${existingPassageFeedbackData.Section}
- Language: ${existingPassageFeedbackData.Targ_Lang_Code}
- Vocabulary: ${wordsArray.join(", ")}

Ensure that the JSON response strictly follows this structure:

{
  "Targ_Lang_Title": "Passage Title",
  "Targ_Lang_Description": "Short description of the passage",
  "Targ_Lang_Passage": "The full text of the passage. Use \\n to indicate line breaks between paragraphs.",
  "Targ_Lang_Question_1": "First question about the passage",
  "Targ_Lang_Answer_1": "The correct answer to Question 1",
  "Targ_Lang_Options_1": [
    "Option A",
    "Option B", 
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_2": "Second question about the passage",
  "Targ_Lang_Answer_2": "The correct answer to Question 2",
  "Targ_Lang_Options_2": [
    "Option A",
    "Option B",
    "Option C", 
    "Option D"
  ],
  "Targ_Lang_Question_3": "Third question about the passage",
  "Targ_Lang_Answer_3": "The correct answer to Question 3",
  "Targ_Lang_Options_3": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_4": "Fourth question about the passage",
  "Targ_Lang_Answer_4": "The correct answer to Question 4",
  "Targ_Lang_Options_4": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ]
}

**Important Instructions:**
- **Use \\n** to indicate line breaks between paragraphs in the "Passage" field.
- **Exactly four options** must be provided for each question.
- One of the options must **explicitly be the correct answer**.
- The JSON response should **strictly adhere** to the provided structure with no additional text or commentary.
- ****All text returned should be in ${existingPassageFeedbackData.Targ_Lang_Code}******.
- **Output only** the JSON object and nothing else.`;

      const payload = {
        messages: [
          {
            role: "user",
            content: feedbackPrompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 2000,
      };

      const result = await callOpenAI(payload);
      if (!result || !result.response) {
        setError("Failed to regenerate passage. Please try again.");
        return;
      }

      let jsonString = result.response;
      const startIndex = jsonString.indexOf("{");
      const endIndex = jsonString.lastIndexOf("}") + 1;
      if (startIndex === -1 || endIndex === 0) {
        setError("Invalid response format from AI. Please try again.");
        return;
      }

      jsonString = jsonString.substring(startIndex, endIndex);

      let parsed;
      try {
        parsed = JSON.parse(jsonString);
      } catch (parseError: any) {
        setError(`Failed to parse regenerated passage: ${parseError.message}`);
        return;
      }

      // Update the passage data
      const updatedPassageData: PassageData = {
        ...existingPassageFeedbackData,
        Targ_Lang_Title: parsed.Targ_Lang_Title || existingPassageFeedbackData.Targ_Lang_Title,
        Targ_Lang_Description: parsed.Targ_Lang_Description || existingPassageFeedbackData.Targ_Lang_Description,
        Targ_Lang_Passage: parsed.Targ_Lang_Passage || existingPassageFeedbackData.Targ_Lang_Passage,
        Targ_Lang_Question_1: parsed.Targ_Lang_Question_1 || existingPassageFeedbackData.Targ_Lang_Question_1,
        Targ_Lang_Answer_1: parsed.Targ_Lang_Answer_1 || existingPassageFeedbackData.Targ_Lang_Answer_1,
        Targ_Lang_Options_1: JSON.stringify(parsed.Targ_Lang_Options_1 || JSON.parse(existingPassageFeedbackData.Targ_Lang_Options_1 || "[]")),
        Targ_Lang_Question_2: parsed.Targ_Lang_Question_2 || existingPassageFeedbackData.Targ_Lang_Question_2,
        Targ_Lang_Answer_2: parsed.Targ_Lang_Answer_2 || existingPassageFeedbackData.Targ_Lang_Answer_2,
        Targ_Lang_Options_2: JSON.stringify(parsed.Targ_Lang_Options_2 || JSON.parse(existingPassageFeedbackData.Targ_Lang_Options_2 || "[]")),
        Targ_Lang_Question_3: parsed.Targ_Lang_Question_3 || existingPassageFeedbackData.Targ_Lang_Question_3,
        Targ_Lang_Answer_3: parsed.Targ_Lang_Answer_3 || existingPassageFeedbackData.Targ_Lang_Answer_3,
        Targ_Lang_Options_3: JSON.stringify(parsed.Targ_Lang_Options_3 || JSON.parse(existingPassageFeedbackData.Targ_Lang_Options_3 || "[]")),
        Targ_Lang_Question_4: parsed.Targ_Lang_Question_4 || existingPassageFeedbackData.Targ_Lang_Question_4,
        Targ_Lang_Answer_4: parsed.Targ_Lang_Answer_4 || existingPassageFeedbackData.Targ_Lang_Answer_4,
        Targ_Lang_Options_4: JSON.stringify(parsed.Targ_Lang_Options_4 || JSON.parse(existingPassageFeedbackData.Targ_Lang_Options_4 || "[]")),
      };

      // Open review modal instead of directly saving
      closeExistingPassageFeedbackModal();
      openReviewModal(existingPassageFeedbackData, updatedPassageData, 'passage');
    } catch (err: any) {
      console.error("Error regenerating existing passage with feedback:", err);
      setError(err.message || "Unknown error during passage regeneration.");
    } finally {
      setIsRegenerating(false);
    }
  }, [existingPassageFeedbackData, existingPassageFeedbackText, selectedWordIds, vocabItems, closeExistingPassageFeedbackModal, section]);

  // Regenerate existing image with feedback
  const handleRegenerateExistingImageWithFeedback = useCallback(async () => {
    if (!existingImageFeedbackData || !existingImageFeedbackText.trim()) {
      setError("Please provide feedback before regenerating the image.");
      return;
    }

    try {
      const cleanPassage = existingImageFeedbackData.Targ_Lang_Passage.replace(/\\n/g, "\n");
      
      // Create feedback-enhanced image prompt
      const feedbackPrompt = `IMPORTANT: THE IMAGE SHOULD NOT CONTAIN ANY TEXT, LETTERS, NUMBERS, OR WRITING. Based on the following feedback, please create an improved image: ${existingImageFeedbackText}

Create an image based on this passage: "${cleanPassage}"

FEEDBACK TO ADDRESS: ${existingImageFeedbackText}

Style requirements:
- Do not include any text, letters, numbers, or writing in the image
- It should be animated yet somewhat realistic
- Make it lively with the subject clearly shown
- Easy to identify for ${existingImageFeedbackData.Targ_Lang_Code} learners
- The image should be lively, engaging, and detailed

THE IMAGE SHOULD NOT CONTAIN ANY TEXT, LETTERS, NUMBERS, OR WRITING ANYWHERE IN THE IMAGE.`;

      const payload = {
        prompt: feedbackPrompt,
        model: "dall-e-3",
        n: 1,
        size: "1024x1024",
        response_format: "url",
      };

      console.log(`Regenerating image with feedback for existing passage ${existingImageFeedbackData.Identifier}...`);
      const imageUrl = await generateImage(payload);
      
      if (!imageUrl) {
        throw new Error("API returned no image URL");
      }

      // Update the passage with new image URL
      const updatedPassageData: PassageData = {
        ...existingImageFeedbackData,
        ImageURL: imageUrl,
      };

      // Open review modal instead of directly saving
      closeExistingImageFeedbackModal();
      openReviewModal(existingImageFeedbackData, updatedPassageData, 'image');
    } catch (err: any) {
      console.error("Error regenerating existing image with feedback:", err);
      setError(err.message || "Unknown error during image regeneration.");
    }
  }, [existingImageFeedbackData, existingImageFeedbackText, closeExistingImageFeedbackModal, section]);

  const handleGenerateImage = useCallback(
    async (index: number) => {
      const passageItem = passagesState[index];
      if (!passageItem || !passageItem.passage) {
        console.error(`No passage at index ${index}`);
        return;
      }
      if (!language) {
        console.error("Language not selected for image gen.");
        return;
      }
      if (passageItem.passage.isRegenerating) {
        console.log(`Image gen already running for index ${index}`);
        return;
      }

      setPassagesState((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, passage: { ...item.passage, isRegenerating: true } }
            : item
        )
      );

      const cleanPassage = passageItem.passage.Targ_Lang_Passage.replace(
        /\\n/g,
        "\n"
      );
      const customPrompt = passageItem.passage.customPrompt?.trim();
      let payload = customPrompt
        ? buildPassageCustomImagePayload(cleanPassage, customPrompt, language)
        : buildPassageInitialImagePromptPayload(cleanPassage, language);

      try {
        console.log(`Generating image for index ${index}...`);
        // Uses imported generateImage
        const imageUrl = await generateImage(payload);
        if (!imageUrl) throw new Error("API returned no image URL");
        setPassagesState((prev) =>
          prev.map((item, i) =>
            i === index
              ? {
                  ...item,
                  passage: {
                    ...item.passage,
                    ImageURL: imageUrl,
                    isRegenerating: false,
                  },
                }
              : item
          )
        );
        console.log(`Image generated for index ${index}.`);
      } catch (error: any) {
        console.error(`Error generating image for index ${index}:`, error);
        setPassagesState((prev) =>
          prev.map((item, i) =>
            i === index
              ? { ...item, passage: { ...item.passage, isRegenerating: false } }
              : item
          )
        ); // Reset flag on error
      }
    },
    [passagesState, language] // build* funcs assumed stable
  );

  const handleImagePromptChange = (index: number, value: string) => {
    setPassagesState((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, passage: { ...item.passage, customPrompt: value } }
          : item
      )
    );
  };

  // --- Generated Passage List Actions ---
  const handleDeleteGeneratedPassage = (index: number) => {
    const passageToDelete = passagesState[index];
    if (
      !window.confirm(
        `Delete unsaved passage for topic "${passageToDelete.topic}"?`
      )
    )
      return;
    setPassagesState((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Edit Modal Logic ---
  const openEditModal = (
    data: GeneratedPassageData | PassageData,
    mode: EditMode,
    index: number | null = null
  ) => {
    clearError();
    setEditingMode(mode);
    setEditingIndex(mode === "new" ? index : null);
    // Deep clone data to prevent modifying original state before saving
    setEditModalData(JSON.parse(JSON.stringify(data)));
    setIsEditModalOpen(true);
    setTimeout(
      () =>
        modalRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        }),
      100
    );
  };

  const handleSaveEdit = async () => {
    clearError();
    if (!editModalData) {
      setError("No data in edit modal.");
      return;
    }
    setIsGenerating(true);

    try {
      if (editingMode === "new" && editingIndex !== null) {
        // Update local state for generated passage
        setPassagesState((prev) =>
          prev.map((item, i) =>
            i === editingIndex
              ? { ...item, passage: editModalData as GeneratedPassageData }
              : item
          )
        );
        console.log(`Local passage ${editingIndex} updated.`);
      } else if (editingMode === "existing") {
        // --- Update existing passage via API (FIX for TS2339) ---
        const { Identifier, Level } = editModalData;
        if (!Identifier || !Level)
          throw new Error("Missing Identifier or Level for update.");

        // Explicitly create the PassageData object for the API
        // Cast editModalData once to potentially access extra fields if needed
        const modalData = editModalData as GeneratedPassageData | PassageData;

        const passageToUpdate: PassageData = {
          Identifier: modalData.Identifier,
          Level: modalData.Level,
          Genre: modalData.Genre,
          Section: modalData.Section,
          Targ_Lang_Code: modalData.Targ_Lang_Code,
          Targ_Lang_Title: modalData.Targ_Lang_Title,
          Targ_Lang_Description: modalData.Targ_Lang_Description,
          Targ_Lang_Passage: modalData.Targ_Lang_Passage,
          Targ_Passage_Word_Timings: modalData.Targ_Passage_Word_Timings,
          Targ_Passage_Audio_URL: modalData.Targ_Passage_Audio_URL,
          Targ_Lang_Question_1: modalData.Targ_Lang_Question_1,
          Targ_Lang_Answer_1: modalData.Targ_Lang_Answer_1,
          Targ_Lang_Options_1: modalData.Targ_Lang_Options_1,
          Targ_Lang_Question_2: modalData.Targ_Lang_Question_2,
          Targ_Lang_Answer_2: modalData.Targ_Lang_Answer_2,
          Targ_Lang_Options_2: modalData.Targ_Lang_Options_2,
          Targ_Lang_Question_3: modalData.Targ_Lang_Question_3,
          Targ_Lang_Answer_3: modalData.Targ_Lang_Answer_3,
          Targ_Lang_Options_3: modalData.Targ_Lang_Options_3,
          Targ_Lang_Question_4: modalData.Targ_Lang_Question_4,
          Targ_Lang_Answer_4: modalData.Targ_Lang_Answer_4,
          Targ_Lang_Options_4: modalData.Targ_Lang_Options_4,
          ImageURL: modalData.ImageURL,
          SelectedWordIds: modalData.SelectedWordIds || "",
          // Use customPrompt if it exists (cast needed), otherwise use existing Prompt
          Prompt:
            (modalData as GeneratedPassageData).customPrompt ||
            modalData.Prompt ||
            "",
        };

        console.log(
          "Attempting to update existing passage:",
          Identifier,
          Level
        );
        // Uses imported updatePassage
        await updatePassage(Identifier, Level, passageToUpdate); // Pass the clean PassageData

        // Refresh existing passages list uses imported fetchPassages
        const fetchedPassages = await fetchPassages(section);
        setExistingPassages(fetchedPassages);
        alert("Passage updated successfully!");
      }
      closeEditModal(); // Close modal on success
    } catch (err: any) {
      console.error(
        `Error saving/updating passage (mode: ${editingMode}):`,
        err
      );
      let errMsg = `Failed to ${
        editingMode === "new" ? "update local data" : "save passage"
      }: `;
      errMsg += err.message || "Unknown error";
      if (err.response)
        errMsg += ` (Server: ${err.response.status})`;
      setError(errMsg);
      // Keep modal open on error
    } finally {
      setIsGenerating(false);
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditModalData(null);
    setEditingIndex(null);
    setEditingMode("new");
  };

  // --- Saving and Deleting (Persistence) ---

  const handleSaveAllGenerated = async () => {
    clearError();
    if (!language || !section) {
      setError("Language and section must be selected.");
      return;
    }
    if (passagesState.length === 0) {
      setError("No generated passages to save.");
      return;
    }
    if (passagesState.some((p) => p.passage.isRegenerating)) {
      setError("Wait for image generation to complete.");
      return;
    }
    if (
      !window.confirm(
        `Save all ${passagesState.length} generated passages to Section "${section}" for ${language}?`
      )
    )
      return;

    setIsGenerating(true);
    const results = { success: 0, fail: 0 };
    const errors: string[] = [];

    for (const { passage, topic } of passagesState) {
      try {
        const { isRegenerating, customPrompt, ...cleanPassage } = passage; // Strip local fields

        const passageToSave: PassageData = {
          ...cleanPassage,
          Identifier: uuidv4(),
          Prompt: customPrompt || "",
          Level: section,
          Section: section,
          Targ_Lang_Code: getLangCode(language),
          Targ_Lang_Title:
            cleanPassage.Targ_Lang_Title || `Generated for ${topic}`,
          Targ_Lang_Description:
            cleanPassage.Targ_Lang_Description || `Description for ${topic}`,
          Targ_Lang_Passage:
            cleanPassage.Targ_Lang_Passage || "Missing passage text.",
          // ← new field:
          SelectedWordIds: cleanPassage.SelectedWordIds!,
        };
        console.log(`Saving passage ID ${passageToSave.Identifier}`);
        // Uses imported addPassage
        await addPassage(passageToSave);
        results.success++;
      } catch (err: any) {
        results.fail++;
        const errorMsg = `Failed to save passage for topic "${topic}": ${
          err.message || "Unknown error"
        }`;
        console.error(errorMsg, err);
        errors.push(errorMsg);
      }
    }
    setIsGenerating(false);

    let feedback = `${results.success} passage(s) saved.`;
    if (results.fail > 0) {
      feedback += ` ${results.fail} failed. Check console.`;
      setError(`Some saves failed:\n- ${errors.join("\n- ")}`);
    } else {
      alert(feedback);
      setPassagesState([]);
      setTopicsState([]);
      setGenres([]);
      // Clear only on full success? Maybe clear even on partial success if desired.
    }

    try {
      // Uses imported fetchPassages
      const fetched = await fetchPassages(section);
      setExistingPassages(fetched); // Refresh list
    } catch (fetchErr: any) {
      console.error("Failed to refresh existing passages:", fetchErr);
      setError(
        (prev) =>
          (prev ? prev + "\n" : "") + `Failed to refresh: ${fetchErr.message}`
      );
    }
  };

  const handleDeleteExistingPassage = async (
    identifier: string,
    level: string
  ) => {
    clearError();
    if (!identifier || !level) {
      setError("Missing Identifier or Level for delete.");
      return;
    }

    const passageToDelete = existingPassages.find(
      (p) => p.Identifier === identifier && p.Level === level
    );
    if (
      !window.confirm(
        `PERMANENTLY DELETE passage "${
          passageToDelete?.Targ_Lang_Title || identifier
        }" (Section: ${level})?`
      )
    )
      return;

    setIsLoadingExisting(true);
    try {
      console.log(`Attempting delete: ID=${identifier}, Level=${level}`);
      // Uses imported deletePassage
      await deletePassage(identifier, level);
      alert("Passage deleted.");
      setExistingPassages((prev) =>
        prev.filter((p) => !(p.Identifier === identifier && p.Level === level))
      ); // Update UI immediately
    } catch (err: any) {
      console.error("Error deleting passage:", err);
      let errMsg = `Failed to delete: ${err.message || "Unknown error"}`;
      if (err.response)
        errMsg += ` (Server: ${err.response.status})`;
      setError(errMsg);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // --- useEffect Hooks ---

  // Fetch sections when language changes
  useEffect(() => {
    async function fetchSectionsForLanguage() {
      if (!language) {
        setSections([]);
        setSection(""); // Clear selected section if language is cleared
        return;
      }

      const langCode = getLangCode(language);
      console.log(`Workspaceing sections for ${language} (Code: ${langCode})`);
      setIsLoadingSections(true);
      clearError();
      setSections([]); // Clear previous sections before fetch
      setSection(""); // Reset section selection

      try {
        // Direct fetch uses the IMPORTED mobileAppUri
        const data = await apiClient.get(
          `${mobileAppUri}/api/v1/all-sections`,
          { params: { Targ_Lang_Code: langCode } }
        );
        console.log("Raw sections data received:", data); // Keep this log

        // *** PARSE THE STRINGIFIED JSON ***
        if (!data || typeof data.sections !== "string") {
          throw new Error("API response is missing the 'sections' string.");
        }

        let sectionsArray;
        try {
          sectionsArray = JSON.parse(data.sections);
          // console.log("Successfully parsed 'sections' string into array:", sectionsArray); // Optional log
        } catch (parseError: any) {
          throw new Error(
            `Failed to parse the 'sections' string from API response: ${parseError.message}`
          );
        }
        // *** END PARSING ***

        if (!Array.isArray(sectionsArray)) {
          throw new Error("Parsed 'sections' data is not an array.");
        }

        // *** MODIFIED LOGIC: Extract 'Identifier' directly ***
        const sectionIdentifiers = new Set<string>(); // Use a Set for automatic uniqueness

        sectionsArray.forEach((sectionItem: any) => {
          // Check if the item has an Identifier property and it's a non-empty string
          if (
            sectionItem &&
            typeof sectionItem.Identifier === "string" &&
            sectionItem.Identifier.trim()
          ) {
            sectionIdentifiers.add(sectionItem.Identifier.trim());
          } else {
            console.warn(
              "Found section item without a valid Identifier:",
              sectionItem
            );
          }
        });

        const uniqueSortedSections = Array.from(sectionIdentifiers).sort();
        console.log(
          `Processed unique section Identifiers: [${uniqueSortedSections.join(
            ", "
          )}]`
        ); // Updated log message
        setSections(uniqueSortedSections);

        if (uniqueSortedSections.length === 0) {
          console.warn(
            `No valid section Identifiers found for ${language} in the response data.`
          );
          // Set error only if the API returned data but we couldn't extract anything
          if (sectionsArray.length > 0) {
            setError(
              `Could not extract valid section names for ${language}, although data was received.`
            );
          } else {
            setError(`API returned no section data items for ${language}.`);
          }
        }
      } catch (error: any) {
        console.error("Error fetching sections:", error);
        setError(`Load sections error: ${error.message}`);
        setSections([]); // Ensure sections are empty on error
        setSection("");
      } finally {
        setIsLoadingSections(false);
      }
    }

    fetchSectionsForLanguage();
  }, [language]); // Depend only on language change

  // Fetch existing passages when section or language changes
  useEffect(() => {
    async function loadExistingPassages() {
      if (!language || !section) {
        setExistingPassages([]);
        return;
      }
      console.log(`Workspaceing existing passages for Section: "${section}"`);
      setIsLoadingExisting(true);
      clearError();

      try {
        // Uses imported fetchPassages
        const fetchedPassages = await fetchPassages(section);
        console.log(`Found ${fetchedPassages.length} existing passages.`);
        setExistingPassages(fetchedPassages);
      } catch (error: any) {
        console.error(
          `Error fetching existing passages for "${section}":`,
          error
        );
        if (error.response?.status === 404) {
          setExistingPassages([]);
          console.log(`No existing passages found (404).`);
        } else {
          setError(
            `Failed loading existing passages: ${
              error.message || "Unknown error"
            }`
          );
          setExistingPassages([]);
        }
      } finally {
        setIsLoadingExisting(false);
      }
    }
    loadExistingPassages();
  }, [section, language]); // Re-run if section OR language changes

  // Trigger initial image generation for new passages
  useEffect(() => {
    if (passagesState.length > 0 && !imagesGeneratedRef.current) {
      imagesGeneratedRef.current = true;
      console.log("Triggering initial image generation...");
      const indicesToGenerate = passagesState
        .map((_, idx) => idx)
        .filter(
          (idx) =>
            !passagesState[idx].passage.ImageURL &&
            !passagesState[idx].passage.isRegenerating
        );
      if (indicesToGenerate.length > 0) {
        console.log(
          `Generating images for indices: ${indicesToGenerate.join(", ")}`
        );
        const generateSequentially = async () => {
          for (const idx of indicesToGenerate) {
            // Check again inside loop in case state changed
            const currentPassage = passagesState.find(
              (p, i) => i === idx
            )?.passage;
            if (
              currentPassage &&
              !currentPassage.ImageURL &&
              !currentPassage.isRegenerating
            ) {
              // Uses imported handleGenerateImage
              await handleGenerateImage(idx);
            }
          }
          console.log("Initial image generation sequence complete.");
        };
        generateSequentially();
      } else {
        console.log("No images need initial generation.");
      }
    }
    if (passagesState.length === 0) imagesGeneratedRef.current = false; // Reset flag if passages cleared
  }, [passagesState, handleGenerateImage]); // Dependency on handleGenerateImage needed

  // --- Render JSX --- (Structure largely unchanged from previous version)
  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Passage Generator</h1>

      {/* Controls Grid */}
      <div className={styles.controlsGrid}>
        <label className={styles.label}>
          {" "}
          <strong>Language:</strong>
          <select
            value={language}
            onChange={handleLanguageChange}
            className={styles.input}
            disabled={isGenerating || isLoadingSections || isLoadingExisting}
          >
            <option value="" disabled>
              -- Select Language --
            </option>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.label}>
          {" "}
          <strong>Section:</strong>
          <select
            value={section}
            onChange={handleSectionChange}
            className={styles.input}
            disabled={
              !language ||
              isLoadingSections ||
              sections.length === 0 ||
              isGenerating ||
              isLoadingExisting
            }
          >
            <option value="" disabled>
              {!language
                ? "Select Language First"
                : isLoadingSections
                ? "Loading..."
                : sections.length === 0
                ? "No sections"
                : "-- Select Section --"}
            </option>
            {sections.map((sec) => (
              <option key={sec} value={sec}>
                {sec}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.label}>
          {" "}
          <strong>Proficiency Level (Gen):</strong>
          <select
            value={proficiency}
            onChange={(e) => setProficiency(e.target.value)}
            className={styles.input}
            disabled={!language || !section || isGenerating}
          >
            {PROFICIENCY_LEVELS.map((lvl) => (
              <option key={lvl} value={lvl}>
                Level {lvl}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.label}>
          {" "}
          <strong>Topics/Genre:</strong>
          <input
            type="number"
            min={1}
            value={topicsCount}
            onChange={(e) =>
              setTopicsCount(Math.max(1, Number(e.target.value)))
            }
            className={styles.input}
            disabled={!language || !section || isGenerating}
          />
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.error}>
          Error: {error}{" "}
          <button onClick={clearError} className={styles.errorClearButton}>
            ×
          </button>
        </div>
      )}

      {/* Genre Management */}
      {language && section && (
        <form onSubmit={handleAddGenre} className={styles.form}>
          <input
            type="text"
            placeholder="Type genre and press Enter or click Add"
            value={newGenre}
            onChange={(e) => setNewGenre(e.target.value)}
            className={styles.input}
            disabled={isGenerating}
          />
          <button
            type="submit"
            className={styles.button}
            disabled={!newGenre.trim() || isGenerating}
          >
            {" "}
            Add Genre{" "}
          </button>
          <div className={styles.wordListContainer}>
            {" "}
            {genres.map((genre, idx) => (
              <span key={genre} className={styles.wordItem}>
                {genre}
                <button
                  type="button"
                  onClick={() => removeGenre(idx)}
                  className={styles.xButton}
                  title={`Remove ${genre}`}
                  disabled={isGenerating}
                >
                  ×
                </button>
              </span>
            ))}{" "}
          </div>
        </form>
      )}

{!loadingVocab && vocabItems.length > 0 && (
  <div className={styles.wordSelector}>
    <h3>Select words to emphasize:</h3>
    <div className={styles.wordListContainer}>
      {vocabItems.map((w) => (
        <label key={w.Identifier} className={styles.wordItem}>
          <input
            type="checkbox"
            value={w.Identifier}
            checked={selectedWordIds.has(w.Identifier)}
            onChange={() =>
              setSelectedWordIds((prev) => {
                const next = new Set(prev);
                if (next.has(w.Identifier)) next.delete(w.Identifier);
                else next.add(w.Identifier);
                return next;
              })
            }
          />
          {w.Targ_Word}
        </label>
      ))}
    </div>
  </div>
)}
{loadingVocab && <p>Loading vocabulary…</p>}



      {/* Topic Generation & Display */}
      {genres.length > 0 && language && section && (
        <>
          <button
            onClick={handleGenerateTopics}
            disabled={isGenerating || isLoadingSections || isLoadingExisting}
            className={styles.button}
            title={`Generate ${topicsCount} topics per genre`}
          >
            {" "}
            {isGenerating ? "Generating Topics..." : `Generate Topics`}{" "}
          </button>
          {topicsState.length > 0 && (
            <div className={styles.sectionBox}>
              {" "}
              <h2>Generated Topics</h2>{" "}
              <table className={styles.table}>
                {" "}
                <thead>
                  <tr>
                    <th>Genre</th>
                    <th>Topics</th>
                    <th>Actions</th>
                  </tr>
                </thead>{" "}
                <tbody>
                  {" "}
                  {topicsState.map((item, idx) => (
                    <tr key={item.genre}>
                      {" "}
                      <td>
                        <strong>{item.genre}</strong>
                      </td>{" "}
                      <td>
                        {" "}
                        {item.topics.length > 0 ? (
                          item.topics.map((topic, i) => (
                            <div key={i}>{topic}</div>
                          ))
                        ) : (
                          <em>No topics.</em>
                        )}{" "}
                      </td>{" "}
                      <td>
                        {" "}
                        <button
                          onClick={() => handleRegenerateTopics(idx)}
                          className={styles.button}
                          disabled={isGenerating}
                          title={`Regenerate for ${item.genre}`}
                        >
                          {" "}
                          Regenerate{" "}
                        </button>{" "}
                      </td>{" "}
                    </tr>
                  ))}{" "}
                </tbody>{" "}
              </table>{" "}
            </div>
          )}
        </>
      )}

      {/* Prompt Preview Section */}
      {topicsState.length > 0 && topicsState.some((ts) => ts.topics.length > 0) && (
        <div className={styles.sectionBox}>
          <h2>Prompt Preview</h2>
          <div style={{ marginBottom: "10px" }}>
            <button
              onClick={() => {
                generatePromptPreview();
                setShowPromptPreview(true);
              }}
              className={styles.button}
              disabled={isGenerating || isLoadingSections || isLoadingExisting}
              title="Preview the prompt that will be used for generation"
            >
              Show/Refresh Prompt Preview
            </button>
          </div>
          
          {showPromptPreview && (
            <div style={{ marginBottom: "15px" }}>
              <label htmlFor="prompt-preview" style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Prompt (editable): {isPromptModified && <span style={{ color: "orange" }}>*Modified*</span>}
              </label>
              <textarea
                id="prompt-preview"
                value={customPrompt}
                onChange={(e) => {
                  setCustomPrompt(e.target.value);
                  setIsPromptModified(true);
                }}
                style={{
                  width: "100%",
                  minHeight: "200px",
                  padding: "10px",
                  fontFamily: "monospace",
                  fontSize: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  resize: "vertical"
                }}
                placeholder="Click 'Show/Refresh Prompt Preview' to see the generated prompt"
              />
              <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                You can edit this prompt before generating passages. Changes will be used for all passage generation.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Passage Generation Button */}
      {topicsState.length > 0 &&
        topicsState.some((ts) => ts.topics.length > 0) && (
          <button
            onClick={handleGeneratePassages}
            disabled={isGenerating || isLoadingSections || isLoadingExisting}
            className={styles.button}
            title="Generate passages from above topics"
          >
            {" "}
            {isGenerating ? "Generating Passages..." : "Generate Passages"}{" "}
          </button>
        )}

      {/* Generated Passages Table & Actions */}
      {passagesState.length > 0 && (
        <div className={styles.sectionBox}>
          {" "}
          <h2>Generated Passages (Unsaved)</h2>{" "}
          <p>
            Lang: {language}, Section: {section}, Prof: {proficiency}
          </p>{" "}
          <table className={`${styles.table} ${styles.passagesTable}`}>
            {" "}
            <thead>
              <tr>
                <th>Genre</th>
                <th>Topic</th>
                <th>Passage Info</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>{" "}
            <tbody>
              {" "}
              {passagesState.map(({ passage, topic }, idx) => (
                <tr key={topic + idx}>
                  {" "}
                  <td>{passage.Genre}</td> <td>{topic}</td>{" "}
                  <td>
                    {" "}
                    <strong>{passage.Targ_Lang_Title}</strong>
                    <br /> <em>{passage.Targ_Lang_Description}</em>
                    <br />{" "}
                    <p className={styles.passageText}>
                      {passage.Targ_Lang_Passage?.replace(/\\n/g, "\n")}
                    </p>{" "}
                    {/* Q&A Blocks */}{" "}
                    {[1, 2, 3, 4].map((num) => {
                      const qKey =
                          `Targ_Lang_Question_${num}` as keyof PassageData,
                        oKey = `Targ_Lang_Options_${num}` as keyof PassageData,
                        aKey = `Targ_Lang_Answer_${num}` as keyof PassageData;
                      const q = passage[qKey],
                        oStr = passage[oKey],
                        a = passage[aKey];
                      let opts: string[] = [];
                      try {
                        if (oStr && typeof oStr === "string")
                          opts = JSON.parse(oStr);
                        if (!Array.isArray(opts)) opts = [];
                      } catch {
                        opts = [];
                      }
                      return q ? (
                        <div key={num} className={styles.questionBlock}>
                          <strong>Q{num}: </strong>
                          {q}
                          <br />
                          <strong>Options:</strong> {opts.join(", ")}
                          <br />
                          <strong className={styles.correctAnswer}>
                            Correct:
                          </strong>{" "}
                          {a}
                        </div>
                      ) : null;
                    })}{" "}
                  </td>{" "}
                  <td className={styles.imageCell}>
                    {" "}
                    {passage.isRegenerating ? (
                      <div className={styles.spinner}></div>
                    ) : passage.ImageURL ? (
                      <img
                        src={passage.ImageURL}
                        alt={topic}
                        className={styles.image}
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    ) : (
                      <p>No image.</p>
                    )}{" "}
                    <input
                      type="text"
                      value={passage.customPrompt || ""}
                      placeholder="Optional custom prompt..."
                      onChange={(e) =>
                        handleImagePromptChange(idx, e.target.value)
                      }
                      className={`${styles.input} ${styles.imageInput}`}
                      disabled={passage.isRegenerating || isGenerating}
                    />{" "}
                    <button
                      onClick={() => handleGenerateImage(idx)}
                      className={styles.button}
                      disabled={passage.isRegenerating || isGenerating}
                      title={
                        passage.isRegenerating
                          ? "Generating..."
                          : passage.ImageURL
                          ? "Regenerate"
                          : "Generate"
                      }
                    >
                      {passage.isRegenerating
                        ? "Generating..."
                        : passage.ImageURL
                        ? "Regen"
                        : "Gen"}
                    </button>{" "}
                    <br />
                    {passage.ImageURL ? (
                      <button
                        onClick={() => {
                          console.log("Image feedback button clicked for index:", idx);
                          openImageFeedbackModal(idx);
                        }}
                        className={styles.button}
                        disabled={passage.isRegenerating || isGenerating}
                        title="Provide feedback and regenerate image"
                        style={{ 
                          backgroundColor: '#ff6b35', 
                          color: 'white', 
                          fontSize: '12px', 
                          padding: '6px 12px',
                          margin: '5px 0',
                          display: 'block',
                          width: '100%'
                        }}
                      >
                        Image Feedback
                      </button>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#999' }}>
                        (Generate image first)
                      </span>
                    )}{" "}
                  </td>{" "}
                  <td className={styles.actionsCell}>
                    {" "}
                    <button
                      onClick={() => openEditModal(passage, "new", idx)}
                      className={`${styles.button} ${styles.editButton}`}
                      disabled={isGenerating}
                    >
                      Edit
                    </button>{" "}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        console.log("Feedback button clicked for index:", idx);
                        openFeedbackModal(idx);
                      }}
                      className={`${styles.button}`}
                      disabled={isGenerating || isRegenerating}
                      title="Provide feedback and regenerate passage"
                      style={{ backgroundColor: '#007bff', color: 'white' }}
                    >
                      Feedback
                    </button>{" "}
                    <button
                      onClick={() => handleDeleteGeneratedPassage(idx)}
                      className={`${styles.button} ${styles.deleteButton}`}
                      disabled={isGenerating}
                    >
                      Delete
                    </button>{" "}
                  </td>{" "}
                </tr>
              ))}{" "}
            </tbody>{" "}
          </table>{" "}
          <button
            onClick={handleSaveAllGenerated}
            className={styles.saveButton}
            disabled={
              isGenerating ||
              passagesState.some((p) => p.passage.isRegenerating)
            }
            title={`Save all ${passagesState.length} generated passages`}
          >
            {" "}
            {isGenerating
              ? "Saving..."
              : `Save All ${passagesState.length}`}{" "}
          </button>{" "}
        </div>
      )}

      {/* Existing Passages Display */}
      {language && section && (
        <div className={styles.sectionBox}>
          {" "}
          <h2>Existing Passages (Section: "{section}")</h2>{" "}
          {isLoadingExisting ? (
            <p>Loading...</p>
          ) : (
            <table className={`${styles.table} ${styles.passagesTable}`}>
              {" "}
              <thead>
                <tr>
                  <th>Genre</th>
                  <th>Target Language Info</th>
                  <th>Image</th>
                  <th>Actions</th>
                </tr>
              </thead>{" "}
              <tbody>
                {existingPassages.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No existing passages found.</td>
                  </tr> // Increased colSpan
                ) : (
                  existingPassages.map((passage) => (
                    <tr key={passage.Identifier}>
                      <td>{passage.Genre}</td>
                      {/* Target Language Info */}
                      <td>
                        <strong>Targ Title:</strong> {passage.Targ_Lang_Title}
                        <br />
                        <em>Targ Desc:</em> {passage.Targ_Lang_Description}
                        <br />
                        <p className={styles.passageText}>
                          <strong>Targ Passage:</strong>
                          <br />
                          {passage.Targ_Lang_Passage?.replace(/\\n/g, "\n")}
                        </p>
                        {/* Targ Q&A Blocks */}
                        {[1, 2, 3, 4].map((num) => {
                          const qKey =
                              `Targ_Lang_Question_${num}` as keyof PassageData,
                            oKey =
                              `Targ_Lang_Options_${num}` as keyof PassageData,
                            aKey =
                              `Targ_Lang_Answer_${num}` as keyof PassageData;
                          const q = passage[qKey],
                            oStr = passage[oKey],
                            a = passage[aKey];
                          let opts: string[] = [];
                          try {
                            if (oStr && typeof oStr === "string")
                              opts = JSON.parse(oStr);
                            if (!Array.isArray(opts)) opts = [];
                          } catch {
                            opts = [];
                          }
                          return q ? (
                            <div
                              key={`targ-q-${num}`}
                              className={styles.questionBlock}
                            >
                              <strong>Targ Q{num}: </strong>
                              {q}
                              <br />
                              <strong>Options:</strong> {opts.join(", ")}
                              <br />
                              <strong className={styles.correctAnswer}>
                                Correct:
                              </strong>{" "}
                              {a}
                            </div>
                          ) : null;
                        })}
                      </td>
                      
                      {/* Image Cell */}
                      <td className={styles.imageCell}>
                        {passage.ImageURL ? (
                          <img
                            src={passage.ImageURL}
                            alt={passage.Identifier}
                            className={styles.image}
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                          />
                        ) : (
                          <p>No Image</p>
                        )}
                      </td>
                      {/* Actions Cell */}
                      <td className={styles.actionsCell}>
                        <button
                          onClick={() => openEditModal(passage, "existing")}
                          className={`${styles.button} ${styles.editButton}`}
                          disabled={isLoadingExisting || isGenerating}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openExistingPassageFeedbackModal(passage)}
                          className={`${styles.button}`}
                          disabled={isLoadingExisting || isGenerating}
                          title="Provide feedback and regenerate this existing passage"
                          style={{ backgroundColor: '#007bff', color: 'white' }}
                        >
                          Passage Feedback
                        </button>
                        {passage.ImageURL && (
                          <button
                            onClick={() => openExistingImageFeedbackModal(passage)}
                            className={`${styles.button}`}
                            disabled={isLoadingExisting || isGenerating}
                            title="Provide feedback and regenerate this existing image"
                            style={{ backgroundColor: '#ff6b35', color: 'white' }}
                          >
                            Image Feedback
                          </button>
                        )}
                        <button
                          onClick={() =>
                            handleDeleteExistingPassage(
                              passage.Identifier,
                              passage.Level
                            )
                          }
                          className={`${styles.button} ${styles.deleteButton}`}
                          disabled={isLoadingExisting || isGenerating}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}{" "}
        </div>
      )}

      {/* --- Edit Modal --- */}
      {isEditModalOpen && editModalData && (
        <div
          ref={modalRef}
          className={styles.modalBackdrop}
          onClick={closeEditModal}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2>
              Edit Passage (
              {editingMode === "new"
                ? `New - ${editModalData.Genre}`
                : `Existing - ${editModalData.Identifier}`}
              )
            </h2>
            <p>Level/Section: {editModalData.Level}</p>

            {/* --- Target Language Fields --- */}
            <fieldset className={styles.modalFieldset}>
              {/* Display Target Language Code */}
              <legend>
                Target Language Info (Code: {editModalData.Targ_Lang_Code})
              </legend>
              <label className={styles.modalLabel}>
                {" "}
                <strong>Title:</strong>
                <input
                  type="text"
                  value={editModalData.Targ_Lang_Title}
                  onChange={(e) =>
                    setEditModalData((prev) =>
                      prev ? { ...prev, Targ_Lang_Title: e.target.value } : null
                    )
                  }
                  className={styles.modalInput}
                />
              </label>
              <label className={styles.modalLabel}>
                {" "}
                <strong>Description:</strong>
                <input
                  type="text"
                  value={editModalData.Targ_Lang_Description}
                  onChange={(e) =>
                    setEditModalData((prev) =>
                      prev
                        ? { ...prev, Targ_Lang_Description: e.target.value }
                        : null
                    )
                  }
                  className={styles.modalInput}
                />
              </label>
              <label className={styles.modalLabel}>
                {" "}
                <strong>Passage Text:</strong>
                <textarea
                  value={editModalData.Targ_Lang_Passage}
                  onChange={(e) =>
                    setEditModalData((prev) =>
                      prev
                        ? { ...prev, Targ_Lang_Passage: e.target.value }
                        : null
                    )
                  }
                  className={styles.modalTextarea}
                  rows={6}
                />
              </label>
              {/* Target Q&A Edit Fields */}
              {[1, 2, 3, 4].map((num) => {
                const qKey = `Targ_Lang_Question_${num}` as keyof PassageData,
                  oKey = `Targ_Lang_Options_${num}` as keyof PassageData,
                  aKey = `Targ_Lang_Answer_${num}` as keyof PassageData;
                const q = editModalData[qKey] || "",
                  oStr = editModalData[oKey] || "[]",
                  a = editModalData[aKey] || "";
                let opts: string[] = [];
                try {
                  opts = JSON.parse(oStr);
                  if (!Array.isArray(opts)) opts = [];
                } catch {
                  opts = [];
                }
                return (
                  <div
                    key={`targ-edit-q-${num}`}
                    className={styles.modalQuestionGroup}
                  >
                    <label className={styles.modalLabel}>
                      {" "}
                      <strong>Targ Question {num}:</strong>{" "}
                      <input
                        type="text"
                        value={q}
                        onChange={(e) =>
                          setEditModalData((prev) =>
                            prev ? { ...prev, [qKey]: e.target.value } : null
                          )
                        }
                        className={styles.modalInput}
                      />{" "}
                    </label>
                    <label className={styles.modalLabel}>
                      {" "}
                      <strong>Targ Options {num} (comma-sep):</strong>{" "}
                      <input
                        type="text"
                        value={opts.join(", ")}
                        onChange={(e) => {
                          const newOpts = e.target.value
                            .split(",")
                            .map((o) => o.trim())
                            .filter((o) => o);
                          setEditModalData((prev) =>
                            prev
                              ? { ...prev, [oKey]: JSON.stringify(newOpts) }
                              : null
                          );
                        }}
                        className={styles.modalInput}
                      />{" "}
                    </label>
                    <label className={styles.modalLabel}>
                      {" "}
                      <strong>Targ Correct Answer {num}:</strong>{" "}
                      <input
                        type="text"
                        value={a}
                        onChange={(e) =>
                          setEditModalData((prev) =>
                            prev ? { ...prev, [aKey]: e.target.value } : null
                          )
                        }
                        className={styles.modalInput}
                      />{" "}
                    </label>
                  </div>
                );
              })}
            </fieldset>

            

            {/* Modal Buttons */}
            <div className={styles.modalButtons}>
              <button
                onClick={handleSaveEdit}
                className={styles.button}
                disabled={isGenerating}
              >
                {" "}
                {isGenerating ? "Saving..." : "Save Changes"}{" "}
              </button>
              <button
                onClick={closeEditModal}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={isGenerating}
              >
                {" "}
                Cancel{" "}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackModalOpen && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} ref={modalRef}>
            <h2>Provide Feedback for Passage Regeneration</h2>
            <p>Debug: Modal is open. Index: {feedbackPassageIndex}</p>
            {feedbackPassageIndex !== null && (
              <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                <strong style={{ fontSize: "16px", color: "#2C5D9A" }}>Current Passage:</strong>
                
                {/* Title */}
                <h3 style={{ margin: "10px 0 5px 0", fontSize: "16px", color: "#1B4D8A" }}>
                  {passagesState[feedbackPassageIndex]?.passage.Targ_Lang_Title}
                </h3>
                
                {/* Description */}
                <p style={{ margin: "0 0 15px 0", fontSize: "14px", color: "#666", fontStyle: "italic" }}>
                  {passagesState[feedbackPassageIndex]?.passage.Targ_Lang_Description}
                </p>
                
                {/* Full Passage Text */}
                <div style={{ marginBottom: "15px" }}>
                  <strong style={{ fontSize: "14px", color: "#2C5D9A" }}>Passage Text:</strong>
                  <div style={{ 
                    margin: "8px 0", 
                    padding: "12px", 
                    backgroundColor: "#ffffff", 
                    border: "1px solid #e9ecef", 
                    borderRadius: "4px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    whiteSpace: "pre-wrap"
                  }}>
                    {passagesState[feedbackPassageIndex]?.passage.Targ_Lang_Passage?.replace(/\\n/g, '\n')}
                  </div>
                </div>

                {/* Questions and Answers */}
                <div>
                  <strong style={{ fontSize: "14px", color: "#2C5D9A" }}>Questions:</strong>
                  {[1, 2, 3, 4].map((num) => {
                    const passage = passagesState[feedbackPassageIndex]?.passage;
                    if (!passage) return null;
                    
                    const qKey = `Targ_Lang_Question_${num}` as keyof typeof passage;
                    const oKey = `Targ_Lang_Options_${num}` as keyof typeof passage;
                    const aKey = `Targ_Lang_Answer_${num}` as keyof typeof passage;
                    
                    const question = passage[qKey];
                    const optionsStr = passage[oKey];
                    const answer = passage[aKey];
                    
                    let options: string[] = [];
                    try {
                      if (optionsStr && typeof optionsStr === 'string') {
                        options = JSON.parse(optionsStr);
                      }
                    } catch {
                      options = [];
                    }
                    
                    if (!question) return null;
                    
                    return (
                      <div key={num} style={{ 
                        margin: "10px 0", 
                        padding: "8px", 
                        backgroundColor: "#ffffff", 
                        border: "1px solid #e9ecef", 
                        borderRadius: "4px" 
                      }}>
                        <strong style={{ fontSize: "13px" }}>Q{num}:</strong> 
                        <span style={{ fontSize: "13px", marginLeft: "5px" }}>{question}</span>
                        {options.length > 0 && (
                          <div style={{ marginTop: "5px", fontSize: "12px" }}>
                            <strong>Options:</strong> {options.join(' | ')}
                          </div>
                        )}
                        {answer && (
                          <div style={{ marginTop: "3px", fontSize: "12px", color: "#28a745" }}>
                            <strong>Answer:</strong> {answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <label className={styles.modalLabel}>
              <strong>Feedback:</strong>
              <span style={{ fontSize: "12px", color: "#666", display: "block", marginTop: "5px" }}>
                Describe what you'd like to improve about this passage (e.g., vocabulary usage, difficulty level, cultural appropriateness, etc.)
              </span>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Enter your feedback here..."
                className={styles.modalTextarea}
                rows={6}
                style={{ marginTop: "10px" }}
              />
            </label>

            <div className={styles.modalButtons}>
              <button
                onClick={handleRegenerateWithFeedback}
                className={styles.button}
                disabled={isRegenerating || !feedbackText.trim()}
                title={!feedbackText.trim() ? "Please enter feedback before regenerating" : "Regenerate passage with feedback"}
              >
                {isRegenerating ? "Regenerating..." : "Regenerate Passage"}
              </button>
              <button
                onClick={closeFeedbackModal}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={isRegenerating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Feedback Modal */}
      {imageFeedbackModalOpen && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} ref={modalRef}>
            <h2>Provide Feedback for Image Regeneration</h2>
            {imageFeedbackPassageIndex !== null && (
              <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                <strong style={{ fontSize: "16px", color: "#2C5D9A" }}>Current Image & Passage:</strong>
                
                {/* Show current image */}
                {passagesState[imageFeedbackPassageIndex]?.passage.ImageURL && (
                  <div style={{ margin: "10px 0", textAlign: "center" }}>
                    <img
                      src={passagesState[imageFeedbackPassageIndex]?.passage.ImageURL}
                      alt="Current passage image"
                      style={{
                        maxWidth: "300px",
                        maxHeight: "200px",
                        border: "2px solid #dee2e6",
                        borderRadius: "8px"
                      }}
                    />
                  </div>
                )}
                
                {/* Title and passage text for context */}
                <h3 style={{ margin: "10px 0 5px 0", fontSize: "16px", color: "#1B4D8A" }}>
                  {passagesState[imageFeedbackPassageIndex]?.passage.Targ_Lang_Title}
                </h3>
                
                <div style={{ 
                  margin: "8px 0", 
                  padding: "12px", 
                  backgroundColor: "#ffffff", 
                  border: "1px solid #e9ecef", 
                  borderRadius: "4px",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  whiteSpace: "pre-wrap",
                  maxHeight: "120px",
                  overflow: "auto"
                }}>
                  {passagesState[imageFeedbackPassageIndex]?.passage.Targ_Lang_Passage?.replace(/\\n/g, '\n')}
                </div>
              </div>
            )}
            
            <label className={styles.modalLabel}>
              <strong>Image Feedback:</strong>
              <span style={{ fontSize: "12px", color: "#666", display: "block", marginTop: "5px" }}>
                Describe issues with the current image (e.g., "remove all text from the image", "make it more culturally appropriate", "improve the visual clarity", etc.)
              </span>
              <textarea
                value={imageFeedbackText}
                onChange={(e) => setImageFeedbackText(e.target.value)}
                placeholder="Enter your image feedback here (e.g., 'The image contains text that needs to be removed')..."
                className={styles.modalTextarea}
                rows={4}
                style={{ marginTop: "10px" }}
              />
            </label>

            <div className={styles.modalButtons}>
              <button
                onClick={handleRegenerateImageWithFeedback}
                className={styles.button}
                disabled={!imageFeedbackText.trim()}
                title={!imageFeedbackText.trim() ? "Please enter feedback before regenerating" : "Regenerate image with feedback"}
              >
                {imageFeedbackPassageIndex !== null && passagesState[imageFeedbackPassageIndex]?.passage.isRegenerating ? "Regenerating..." : "Regenerate Image"}
              </button>
              <button
                onClick={closeImageFeedbackModal}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={imageFeedbackPassageIndex !== null && passagesState[imageFeedbackPassageIndex]?.passage.isRegenerating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Passage Feedback Modal */}
      {existingPassageFeedbackModalOpen && existingPassageFeedbackData && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} ref={modalRef}>
            <h2>Provide Feedback for Existing Passage Regeneration</h2>
            <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
              <strong style={{ fontSize: "16px", color: "#2C5D9A" }}>Current Passage:</strong>
              
              <h3 style={{ margin: "10px 0 5px 0", fontSize: "16px", color: "#1B4D8A" }}>
                {existingPassageFeedbackData.Targ_Lang_Title}
              </h3>
              
              <p style={{ margin: "0 0 15px 0", fontSize: "14px", color: "#666", fontStyle: "italic" }}>
                {existingPassageFeedbackData.Targ_Lang_Description}
              </p>
              
              <div style={{ 
                margin: "8px 0", 
                padding: "12px", 
                backgroundColor: "#ffffff", 
                border: "1px solid #e9ecef", 
                borderRadius: "4px",
                fontSize: "14px",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                maxHeight: "150px",
                overflow: "auto"
              }}>
                {existingPassageFeedbackData.Targ_Lang_Passage?.replace(/\\n/g, '\n')}
              </div>
            </div>
            
            <label className={styles.modalLabel}>
              <strong>Feedback:</strong>
              <span style={{ fontSize: "12px", color: "#666", display: "block", marginTop: "5px" }}>
                Describe what you'd like to improve about this existing passage
              </span>
              <textarea
                value={existingPassageFeedbackText}
                onChange={(e) => setExistingPassageFeedbackText(e.target.value)}
                placeholder="Enter your feedback here..."
                className={styles.modalTextarea}
                rows={6}
                style={{ marginTop: "10px" }}
              />
            </label>

            <div className={styles.modalButtons}>
              <button
                onClick={handleRegenerateExistingPassageWithFeedback}
                className={styles.button}
                disabled={isRegenerating || !existingPassageFeedbackText.trim()}
                title={!existingPassageFeedbackText.trim() ? "Please enter feedback before regenerating" : "Regenerate existing passage with feedback"}
              >
                {isRegenerating ? "Regenerating..." : "Regenerate Existing Passage"}
              </button>
              <button
                onClick={closeExistingPassageFeedbackModal}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={isRegenerating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Image Feedback Modal */}
      {existingImageFeedbackModalOpen && existingImageFeedbackData && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} ref={modalRef}>
            <h2>Provide Feedback for Existing Image Regeneration</h2>
            <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
              <strong style={{ fontSize: "16px", color: "#2C5D9A" }}>Current Image & Passage:</strong>
              
              {existingImageFeedbackData.ImageURL && (
                <div style={{ margin: "10px 0", textAlign: "center" }}>
                  <img
                    src={existingImageFeedbackData.ImageURL}
                    alt="Current existing passage image"
                    style={{
                      maxWidth: "300px",
                      maxHeight: "200px",
                      border: "2px solid #dee2e6",
                      borderRadius: "8px"
                    }}
                  />
                </div>
              )}
              
              <h3 style={{ margin: "10px 0 5px 0", fontSize: "16px", color: "#1B4D8A" }}>
                {existingImageFeedbackData.Targ_Lang_Title}
              </h3>
              
              <div style={{ 
                margin: "8px 0", 
                padding: "12px", 
                backgroundColor: "#ffffff", 
                border: "1px solid #e9ecef", 
                borderRadius: "4px",
                fontSize: "13px",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
                maxHeight: "120px",
                overflow: "auto"
              }}>
                {existingImageFeedbackData.Targ_Lang_Passage?.replace(/\\n/g, '\n')}
              </div>
            </div>
            
            <label className={styles.modalLabel}>
              <strong>Image Feedback:</strong>
              <span style={{ fontSize: "12px", color: "#666", display: "block", marginTop: "5px" }}>
                Describe issues with the current image for this existing passage
              </span>
              <textarea
                value={existingImageFeedbackText}
                onChange={(e) => setExistingImageFeedbackText(e.target.value)}
                placeholder="Enter your image feedback here..."
                className={styles.modalTextarea}
                rows={4}
                style={{ marginTop: "10px" }}
              />
            </label>

            <div className={styles.modalButtons}>
              <button
                onClick={handleRegenerateExistingImageWithFeedback}
                className={styles.button}
                disabled={!existingImageFeedbackText.trim()}
                title={!existingImageFeedbackText.trim() ? "Please enter feedback before regenerating" : "Regenerate existing image with feedback"}
              >
                Regenerate Existing Image
              </button>
              <button
                onClick={closeExistingImageFeedbackModal}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal for Regenerated Content */}
      {reviewModalOpen && originalPassage && regeneratedPassage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} style={{ maxWidth: "90vw", width: "1000px" }}>
            <h2>Review {reviewType === 'passage' ? 'Regenerated Passage' : 'Regenerated Image'}</h2>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              Compare the original and regenerated content. Choose to approve or reject the changes.
            </p>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
              {/* Original Content */}
              <div style={{ border: "2px solid #dc3545", borderRadius: "8px", padding: "15px", backgroundColor: "#fff5f5" }}>
                <h3 style={{ margin: "0 0 15px 0", color: "#dc3545", fontSize: "18px" }}>Original</h3>
                
                {reviewType === 'image' && originalPassage.ImageURL && (
                  <div style={{ marginBottom: "15px", textAlign: "center" }}>
                    <img
                      src={originalPassage.ImageURL}
                      alt="Original"
                      style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "4px", border: "1px solid #ddd" }}
                    />
                  </div>
                )}
                
                <div><strong>Title:</strong> {originalPassage.Targ_Lang_Title}</div>
                <div style={{ margin: "10px 0" }}><strong>Description:</strong> {originalPassage.Targ_Lang_Description}</div>
                <div style={{ 
                  backgroundColor: "#ffffff", 
                  padding: "10px", 
                  borderRadius: "4px", 
                  border: "1px solid #ddd",
                  whiteSpace: "pre-wrap",
                  fontSize: "13px",
                  lineHeight: "1.4",
                  maxHeight: "200px",
                  overflow: "auto"
                }}>
                  {originalPassage.Targ_Lang_Passage?.replace(/\\n/g, '\n')}
                </div>
              </div>

              {/* Regenerated Content */}
              <div style={{ border: "2px solid #28a745", borderRadius: "8px", padding: "15px", backgroundColor: "#f8fff9" }}>
                <h3 style={{ margin: "0 0 15px 0", color: "#28a745", fontSize: "18px" }}>Regenerated</h3>
                
                {reviewType === 'image' && regeneratedPassage.ImageURL && (
                  <div style={{ marginBottom: "15px", textAlign: "center" }}>
                    <img
                      src={regeneratedPassage.ImageURL}
                      alt="Regenerated"
                      style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "4px", border: "1px solid #ddd" }}
                    />
                  </div>
                )}
                
                <div><strong>Title:</strong> {regeneratedPassage.Targ_Lang_Title}</div>
                <div style={{ margin: "10px 0" }}><strong>Description:</strong> {regeneratedPassage.Targ_Lang_Description}</div>
                <div style={{ 
                  backgroundColor: "#ffffff", 
                  padding: "10px", 
                  borderRadius: "4px", 
                  border: "1px solid #ddd",
                  whiteSpace: "pre-wrap",
                  fontSize: "13px",
                  lineHeight: "1.4",
                  maxHeight: "200px",
                  overflow: "auto"
                }}>
                  {regeneratedPassage.Targ_Lang_Passage?.replace(/\\n/g, '\n')}
                </div>
              </div>
            </div>

            {/* Questions & Answers Comparison (only for passage reviews) */}
            {reviewType === 'passage' && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ margin: "0 0 15px 0", color: "#1B4D8A" }}>Questions & Answers Comparison</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  {[1, 2, 3, 4].map((num) => {
                    const qKey = `Targ_Lang_Question_${num}` as keyof PassageData;
                    const aKey = `Targ_Lang_Answer_${num}` as keyof PassageData;
                    const oKey = `Targ_Lang_Options_${num}` as keyof PassageData;
                    
                    const origQ = originalPassage[qKey];
                    const regenQ = regeneratedPassage[qKey];
                    
                    if (!origQ && !regenQ) return null;
                    
                    return (
                      <div key={num} style={{ gridColumn: "1 / -1", marginBottom: "15px" }}>
                        <h5 style={{ margin: "0 0 10px 0", color: "#2C5D9A" }}>Question {num}</h5>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                          <div style={{ padding: "10px", backgroundColor: "#fff5f5", borderRadius: "4px", border: "1px solid #dc3545" }}>
                            <div><strong>Q:</strong> {origQ}</div>
                            <div><strong>A:</strong> {originalPassage[aKey]}</div>
                          </div>
                          <div style={{ padding: "10px", backgroundColor: "#f8fff9", borderRadius: "4px", border: "1px solid #28a745" }}>
                            <div><strong>Q:</strong> {regenQ}</div>
                            <div><strong>A:</strong> {regeneratedPassage[aKey]}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={styles.modalButtons}>
              <button
                onClick={handleApproveRegeneration}
                className={styles.button}
                style={{ backgroundColor: "#28a745", color: "white" }}
                title="Save the regenerated content"
              >
                Approve & Save Changes
              </button>
              <button
                onClick={handleRejectRegeneration}
                className={`${styles.button} ${styles.cancelButton}`}
                style={{ backgroundColor: "#dc3545", color: "white" }}
                title="Discard the regenerated content"
              >
                Reject Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div> // End container
  );
};

export default Passages;
